import { useCallback, useEffect, useState } from 'react'
import { buildDefaultState, normalizeState } from '../../../core/persistence'
import type { PersistedState, AppState } from '../../../core/persistence'
import { getPersistenceClient } from '../runtime/persistenceClient'
import { appStateFromPersisted, normalizeAlbumRecordTheme, persistedFromAppState } from './state'

const STORAGE_KEY = 'investfacil-state-backup-v2'
const persistence = getPersistenceClient()

function saveLocalBackup(state: AppState): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ updatedAt: new Date().toISOString(), state: persistedFromAppState(state) })
    )
  } catch {
    /* non-fatal */
  }
}

function loadLocalBackup(): AppState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state: PersistedState }
    const normalized = normalizeState(parsed.state)
    return appStateFromPersisted({
      ...normalized,
      albums: normalized.albums.map(normalizeAlbumRecordTheme)
    })
  } catch {
    return null
  }
}

export function usePersistedAppState(saveErrorMessage: string) {
  const [appState, setAppState] = useState<AppState>(() =>
    appStateFromPersisted(buildDefaultState())
  )
  const [isHydrated, setIsHydrated] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const persistState = useCallback((nextState: AppState): void => {
    saveLocalBackup(nextState)
    persistence.saveStateSync(persistedFromAppState(nextState))
  }, [])

  useEffect(() => {
    async function hydrate(): Promise<void> {
      try {
        const raw = await persistence.loadState()
        const loaded = raw ? normalizeState(raw) : buildDefaultState()
        let resolved = appStateFromPersisted({
          ...loaded,
          albums: loaded.albums.map(normalizeAlbumRecordTheme)
        })
        if (resolved.albums.length === 0) {
          const backup = loadLocalBackup()
          if (backup && backup.albums.length > 0) resolved = backup
        }
        setAppState(resolved)
      } catch (err) {
        console.error('[InvestAlbum] Hydration failed:', err)
        const backup = loadLocalBackup()
        if (backup) setAppState(backup)
      } finally {
        setIsHydrated(true)
      }
    }
    void hydrate()
  }, [])

  useEffect(() => {
    if (!isHydrated) return
    persistence
      .saveState(persistedFromAppState(appState))
      .then(() => {
        setSaveError(null)
      })
      .catch((err: unknown) => {
        console.error('[InvestAlbum] Async save failed:', err)
        setSaveError(saveErrorMessage)
      })

    saveLocalBackup(appState)
  }, [appState, isHydrated, saveErrorMessage])

  useEffect(() => {
    if (!isHydrated) return
    const handleBeforeUnload = (): void => {
      persistState(appState)
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [appState, isHydrated, persistState])

  function retrySave(): void {
    persistence
      .saveState(persistedFromAppState(appState))
      .then(() => {
        setSaveError(null)
      })
      .catch((err: unknown) => {
        console.error('[InvestAlbum] Retry save failed:', err)
        setSaveError(saveErrorMessage)
      })
  }

  return {
    appState,
    setAppState,
    isHydrated,
    saveError,
    setSaveError,
    retrySave
  }
}
